import React from 'react';
import { Stack, Typography, TooltipProps } from '@mui/material';
import DosenTooltip from '../../../components/ui/DosenTooltip';
import { formatNumberWithCommas } from './common';
import MySkeleton from '../../../components/Skeleton';
import upvotesIcon from '../../../assets/images/upvotes.svg';
import xpPointIcon from '../../../assets/images/favicon.svg';
import HandshakeIcon from '../../../assets/images/handshake.svg';
import thumbsDownIcon from '../../../assets/images/thumbsdownimg.svg';
import groupChatIcon from '../../../assets/images/GroupChatUsers.svg';
import greenThumbsUpIcon from '../../../assets/images/greenthumbsup.svg';
import useResponsiveScreenSwitch from '../../../utils/useResponsiveScreenSwitch';

interface UpvotesProps {
  number?: number;
  icon?: 'xpPoint' | 'group' | 'greenThumbsup' | 'dislike' | 'match' | string;
  width?: string;
  color?: string;
  maxWidth?: string;
  printArea?: 'myMentorCard' | 'header' | string;
  toolTip?: boolean;
  usertype?: string;
  loading?: boolean;
  placement?: TooltipProps['placement'];
}

// Icons
const ICONS: Record<string, string> = {
  xpPoint: xpPointIcon,
  group: groupChatIcon,
  greenThumbsup: greenThumbsUpIcon,
  dislike: thumbsDownIcon,
  default: upvotesIcon,
  match: HandshakeIcon
};

// Tooltip text
const TOOLTIP_TEXTS: Record<string, string> = {
  xpPoint:
    'These are your experience points (XP) - You earn them by using your Dosen account e.g., setting up your account, sending messages, having calls, reading library articles.',
  match: 'Number of matches',
  default:
    'These are your Upvotes - Other people in your program can recognize your work by "upvoting" you after calls or from your profile.'
};

const Upvotes: React.FC<UpvotesProps> = ({
  number = 0,
  icon = '',
  width,
  maxWidth,
  printArea,
  toolTip = false,
  usertype,
  loading = false,
  color,
  placement = 'bottom'
}) => {
  const { forMobile } = useResponsiveScreenSwitch();

  const selectedIcon = ICONS[icon] || ICONS.default;

  // Font color
  const fontColor = color
    ? color
    : printArea === 'myMentorCard'
    ? '#11895E'
    : printArea === 'header' && forMobile
    ? '#FFFFFF'
    : 'secondary';

  // Border style
  const borderStyle = color
    ? `1px solid ${color}`
    : printArea === 'myMentorCard'
    ? '1px solid #11895E'
    : printArea === 'header' && forMobile
    ? '1px solid transparent'
    : '1px solid #EFF0F4';

  // Tooltip
  const tooltipTitle = toolTip ? TOOLTIP_TEXTS[icon] || TOOLTIP_TEXTS.default : '';

  if (loading) {
    return <MySkeleton variant="rounded" width={50} height={16} />;
  }

  // Content
  const content = (
    <Stack
      direction="row"
      alignItems="center"
      border={borderStyle}
      spacing={0.5}
      px={0.5}
      borderRadius={0.5}
      width={width || 'fit-content'}
      maxWidth={maxWidth || 'fit-content'}
    >
      <img src={selectedIcon} alt="icon" width="11" height="11" />
      <Typography fontWeight={600} fontSize="12px" color={fontColor} noWrap>
        {formatNumberWithCommas(number)}
      </Typography>
    </Stack>
  );

  return toolTip ? (
    <DosenTooltip title={tooltipTitle} placement={placement}>
      {content}
    </DosenTooltip>
  ) : (
    content
  );
};

export default Upvotes;
